import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfilePreview = ({ businessProfile, userProfile, products }) => {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [visibilitySettings, setVisibilitySettings] = useState({
    company_info: true,
    contact_details: true,
    solutions: true,
    team_info: true,
    technologies: true,
    partnerships: false
  });

  const previewModes = [
    { id: 'desktop', label: 'Desktop View', icon: 'Monitor' },
    { id: 'tablet', label: 'Tablet View', icon: 'Tablet' },
    { id: 'mobile', label: 'Mobile View', icon: 'Smartphone' }
  ];

  const getPreviewStyles = () => {
    switch (previewMode) {
      case 'tablet':
        return 'max-w-2xl mx-auto';
      case 'mobile':
        return 'max-w-sm mx-auto';
      default:
        return 'max-w-6xl mx-auto';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'prototype': return 'bg-yellow-100 text-yellow-800';
      case 'pilot': return 'bg-blue-100 text-blue-800';
      case 'deployed': return 'bg-green-100 text-green-800';
      case 'scaled': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleVisibilityToggle = (section) => {
    setVisibilitySettings(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Profile Preview</h3>
          <p className="text-sm text-gray-600">
            See how your business profile appears to potential partners and visitors.
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Preview Mode Selector */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            {previewModes?.map((mode) => (
              <button
                key={mode?.id}
                onClick={() => setPreviewMode(mode?.id)}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  previewMode === mode?.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name={mode?.icon} className="w-4 h-4 mr-1" />
                {mode?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Visibility Controls */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Visibility Settings</h4>
        <p className="text-sm text-gray-600 mb-4">
          Control which sections are visible to different types of visitors.
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(visibilitySettings)?.map(([key, visible]) => (
            <label key={key} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={visible}
                onChange={() => handleVisibilityToggle(key)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 capitalize">
                {key?.replace('_', ' ')}
              </span>
            </label>
          ))}
        </div>
      </div>
      {/* Preview Container */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
            <div className="ml-4 flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500">
              smartcities.directory/business/{businessProfile?.name?.toLowerCase()?.replace(/\s+/g, '-')}
            </div>
          </div>
        </div>

        <div className={`${getPreviewStyles()} transition-all duration-300`}>
          <div className="p-6 space-y-8">
            {/* Header Section */}
            {visibilitySettings?.company_info && (
              <div className="text-center space-y-4">
                {businessProfile?.logo_url && (
                  <img
                    src={businessProfile?.logo_url}
                    alt={businessProfile?.name}
                    className="w-20 h-20 rounded-lg object-cover mx-auto border"
                  />
                )}
                
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {businessProfile?.name || 'Your Company Name'}
                  </h1>
                  
                  <div className="flex items-center justify-center space-x-4 text-sm text-gray-600 mb-4">
                    {businessProfile?.category && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 capitalize">
                        {businessProfile?.category?.replace('_', ' ')}
                      </span>
                    )}
                    
                    {businessProfile?.is_verified && (
                      <span className="inline-flex items-center text-green-600">
                        <Icon name="CheckCircle" className="w-4 h-4 mr-1" />
                        Verified
                      </span>
                    )}
                    
                    {businessProfile?.is_featured && (
                      <span className="inline-flex items-center text-purple-600">
                        <Icon name="Star" className="w-4 h-4 mr-1" />
                        Featured
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-600 max-w-2xl mx-auto">
                    {businessProfile?.description || 'Add a compelling company description to attract potential partners...'}
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center justify-center space-x-8 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">{businessProfile?.view_count || 0}</div>
                    <div className="text-gray-600">Profile Views</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">{products?.length || 0}</div>
                    <div className="text-gray-600">Solutions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">{businessProfile?.founded_year ? new Date()?.getFullYear() - businessProfile?.founded_year : 'N/A'}</div>
                    <div className="text-gray-600">Years Active</div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            {visibilitySettings?.contact_details && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {businessProfile?.website_url && (
                  <a
                    href={businessProfile?.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                  >
                    <Icon name="Globe" className="w-4 h-4 mr-2" />
                    <span className="text-sm">Website</span>
                  </a>
                )}
                
                {businessProfile?.contact_email && (
                  <a
                    href={`mailto:${businessProfile?.contact_email}`}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                  >
                    <Icon name="Mail" className="w-4 h-4 mr-2" />
                    <span className="text-sm">Email</span>
                  </a>
                )}
                
                {businessProfile?.contact_phone && (
                  <a
                    href={`tel:${businessProfile?.contact_phone}`}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                  >
                    <Icon name="Phone" className="w-4 h-4 mr-2" />
                    <span className="text-sm">Call</span>
                  </a>
                )}
                
                <Button className="w-full">
                  <Icon name="MessageSquare" className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </div>
            )}

            {/* Services & Technologies */}
            {visibilitySettings?.technologies && (
              <div className="space-y-6">
                {businessProfile?.services?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Services</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {businessProfile?.services?.map((service, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3 text-center">
                          <span className="text-sm font-medium text-gray-900">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {businessProfile?.technologies?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Core Technologies</h3>
                    <div className="flex flex-wrap gap-2">
                      {businessProfile?.technologies?.map((tech, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Solutions Portfolio */}
            {visibilitySettings?.solutions && products?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Solutions Portfolio</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products?.slice(0, 6)?.map((product) => (
                    <div key={product?.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{product?.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadgeColor(product?.status)}`}>
                          {product?.status}
                        </span>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product?.description}</p>
                      
                      {product?.technologies_used?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {product?.technologies_used?.slice(0, 3)?.map((tech, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white text-gray-700">
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-3 text-gray-500">
                          <span className="flex items-center">
                            <Icon name="Eye" className="w-4 h-4 mr-1" />
                            {product?.view_count || 0}
                          </span>
                          <span className="flex items-center">
                            <Icon name="Heart" className="w-4 h-4 mr-1" />
                            {product?.like_count || 0}
                          </span>
                        </div>
                        <button className="text-blue-600 hover:text-blue-800">Learn More</button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {products?.length > 6 && (
                  <div className="text-center mt-6">
                    <Button variant="outline">
                      View All Solutions ({products?.length})
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Team Information */}
            {visibilitySettings?.team_info && userProfile && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Team Member</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                      <Icon name="User" className="w-8 h-8 text-gray-600" />
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{userProfile?.full_name || 'Add your name'}</h4>
                      <p className="text-blue-600 text-sm">{userProfile?.title || 'Add your title'}</p>
                      
                      {userProfile?.bio && (
                        <p className="text-gray-600 text-sm mt-2">{userProfile?.bio}</p>
                      )}
                      
                      {userProfile?.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {userProfile?.skills?.slice(0, 4)?.map((skill, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white text-gray-700">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {userProfile?.linkedin_url && (
                        <a
                          href={userProfile?.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm mt-3"
                        >
                          <Icon name="Linkedin" className="w-4 h-4 mr-1" />
                          LinkedIn Profile
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Location & Company Details */}
            {(businessProfile?.city || businessProfile?.country || businessProfile?.employee_count_range) && (
              <div className="border-t pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                  {(businessProfile?.city || businessProfile?.country) && (
                    <div className="flex items-center">
                      <Icon name="MapPin" className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">
                        {[businessProfile?.city, businessProfile?.country]?.filter(Boolean)?.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {businessProfile?.employee_count_range && (
                    <div className="flex items-center">
                      <Icon name="Users" className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">{businessProfile?.employee_count_range}</span>
                    </div>
                  )}
                  
                  {businessProfile?.founded_year && (
                    <div className="flex items-center">
                      <Icon name="Calendar" className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">Founded in {businessProfile?.founded_year}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* SEO Preview */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">SEO Preview</h4>
        <p className="text-sm text-gray-600 mb-4">
          How your business profile might appear in search engine results.
        </p>
        
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-blue-600 text-lg hover:underline cursor-pointer">
            {businessProfile?.name || 'Your Company Name'} | Smart Cities Directory
          </div>
          <div className="text-green-600 text-sm">
            https://smartcities.directory/business/{businessProfile?.name?.toLowerCase()?.replace(/\s+/g, '-')}
          </div>
          <div className="text-gray-700 text-sm mt-1">
            {businessProfile?.description?.substring(0, 150) || 'Add a compelling company description...'}
            {businessProfile?.description?.length > 150 && '...'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePreview;